/*>
* @license
* Copyright (c) 2014-2022 ABILITY Network, Inc., an INOVALON, Inc. company. All Rights Reserved.
* 
* This computer program is CONFIDENTIAL and a TRADE SECRET of ABILITY Network, Inc.
* The receipt or possession of this program does not convey any rights to use,
* reproduce or disclose its contents in whole or in part, without the specific
* written consent of ABILITY Network, Inc. Any use, reproduction or disclosure of
* this program without the express written consent of ABILITY Network, Inc. is a
* violation of the copyright laws and may subject you to criminal prosecution.
<*/
import axios from 'axios'
import { injectable } from 'inversify'
import { injectProperty } from '../../configuration/ServiceLocator'
import { SettingsManager } from '../../configuration/SettingsManager'
import { ErrorHelper } from '../../utils/ErrorHelper'
import { UserVote, IArticle, IArticleRating } from '../ContentStateManager/contracts'
import { IUserSessionMiddleware } from '../middleware/UserSessionMiddleware/contracts'
import { IContentApiClient } from './contracts'

@injectable()
export class ContentApiClient implements IContentApiClient {
	@injectProperty('IUserSessionMiddleware')
	private readonly userSessionMiddleware!: IUserSessionMiddleware

	getArticleQuery(path: string): string {
		return `query MyQuery {
  					aBILITYPage(where: {path: "${path}"}) {
						contentItemId
						path
						displayText
						publishedUtc
						wysiwygContent {
							markup
						}
						productContent {
							productName
						}
						sectionTags {
							termContentItems {
							  createdUtc
							  displayText
							  modifiedUtc
							  publishedUtc
							  contentItemId
							}
						}
  					}
				}`
	}

	getArticle(path: string): Promise<IArticle> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}`
		return new Promise<IArticle>((resolve, reject) => {
			this.userSessionMiddleware.loadSessionJwt(false).then(x =>
				axios
					.get(url, {
						params: {
							query: this.getArticleQuery(path),
						},
						headers: {
							Authorization: `Bearer ${x}`,
							"Cache-Control": "no-cache, no-store"
						},
					})
					.then(response => {
						if (response.data.data.aBILITYPage.length > 0) {
							const page = response.data.data.aBILITYPage[0]
							const article = {
								contentItemId: page.contentItemId,
								path: page.path,
								displayText: page.displayText,
								publishedUtc: new Date(page.publishedUtc),
								markup: page.wysiwygContent.markup,
								productCode: page.productContent.productName,
								sectionId: page.sectionTags.termContentItems.length > 0 ? page.sectionTags.termContentItems[0].contentItemId: '',
								sectionName: page.sectionTags.termContentItems.length > 0 ? page.sectionTags.termContentItems[0].displayText: ''
							} as IArticle
							resolve(article)
						} else {
							reject(`Help page with path "${path}" not found`)
						}
					})
					.catch(error => {
						if (error.response !== null && typeof error.response !== 'undefined' && error.response.status === 401) ErrorHelper.handleApiError(error)
						else reject(error)
					}),
			)
		})
	}

	getRating(contentItemId: string, userId: string): Promise<IArticleRating> {
		return new Promise<IArticleRating>((resolve, reject) => {
			const url = `${SettingsManager.REACT_APP_USER_VOTE_API_URL().value}`
			this.userSessionMiddleware.loadSessionJwt(false).then(x =>
				axios
					.get(url, {
						params: {
							articleId: contentItemId,
							userId: userId,
						},
						headers: {
							Authorization: `Bearer ${x}`,
							"Cache-Control": "no-cache, no-store"
						},
					})
					.then(response => {
						const rating = response.data.item
						const articleRating = {
							contentItemId: rating.contentItemId,
							countLikes: rating.totalLikes,
							countDislikes: rating.totalDislikes,
							vote: rating.vote,
						} as IArticleRating
						resolve(articleRating)
					})
					.catch(error => {
						if (error.response !== null && typeof error.response !== 'undefined' && error.response.status === 401) ErrorHelper.handleApiError(error)
						else reject(error)
					}),
			)
		})
	}

	vote(contentItemId: string, userId: string, vote: UserVote): Promise<IArticleRating> {
		return new Promise<IArticleRating>((resolve, reject) => {
			const url = `${SettingsManager.REACT_APP_USER_VOTE_API_URL().value}`
			const userVote = vote === UserVote.like ? true : false
			this.userSessionMiddleware.loadSessionJwt(false).then(x =>
				axios
					.post(
						url,
						{
							contentItemId: contentItemId,
							userId: userId,
							vote: vote,
						},
						{
							headers: {
								Authorization: `Bearer ${x}`,
								"Content-Type": "application/json"
							},
						},
					)
					.then(response => {
						const rating = response.data.item
						const articleRating = {
							contentItemId: rating.contentItemId,
							countLikes: rating.totalLikes,
							countDislikes: rating.totalDislikes,
							vote: rating.vote,
						} as IArticleRating
						resolve(articleRating)
					})
					.catch(error => {
						if (error.response !== null && typeof error.response !== 'undefined' && error.response.status === 401) ErrorHelper.handleApiError(error)
						else reject(error)
					}),
			)
		})
	}

	deleteVote(contentItemId: string, userId: string, vote: UserVote): Promise<IArticleRating> {
		return new Promise<IArticleRating>((resolve, reject) => {
			const url = `${SettingsManager.REACT_APP_USER_VOTE_API_URL().value}`
			this.userSessionMiddleware.loadSessionJwt(false).then(x =>
				axios
					.delete(url, {
						headers: {
							Authorization: `Bearer ${x}`,
						},
						params: {
							articleId: contentItemId,
							userId: userId,
						},
					})
					.then(response => {
						const rating = response.data.item
						const articleRating = {
							contentItemId: rating.contentItemId,
							countLikes: rating.totalLikes,
							countDislikes: rating.totalDislikes,
							vote: rating.vote,
						} as IArticleRating
						resolve(articleRating)
					})
					.catch(error => {
						if (error.response !== null && typeof error.response !== 'undefined' && error.response.status === 401) ErrorHelper.handleApiError(error)
						else reject(error)
					}),
			)
		})
	}

	changeVote(contentItemId: string, userId: string, vote: UserVote): Promise<IArticleRating> {
		return new Promise<IArticleRating>((resolve, reject) => {
			const url = `${SettingsManager.REACT_APP_USER_VOTE_API_URL().value}`
			this.userSessionMiddleware.loadSessionJwt(false).then(x =>
				axios
					.put(
						url,
						{
							contentItemId: contentItemId,
							userId: userId,
							vote: vote,
						},
						{
							headers: {
								Authorization: `Bearer ${x}`,
								"Content-Type": "application/json"
							},
						},
					)
					.then(response => {
						const rating = response.data.item
						const articleRating = {
							contentItemId: rating.contentItemId,
							countLikes: rating.totalLikes,
							countDislikes: rating.totalDislikes,
							vote: rating.vote,
						} as IArticleRating
						resolve(articleRating)
					})
					.catch(error => {
						if (error.response !== null && typeof error.response !== 'undefined' && error.response.status === 401) ErrorHelper.handleApiError(error)
						else reject(error)
					}),
			)
		})
	}
}
